import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { supabase } from "@/lib/supabase";
import { useAuth, loadUserProfile } from "@/contexts/AuthContext";
import { T, F, Over, Wordmark, th as palette } from "@/lib/bricks";
import { RESEND_COOLDOWN_SECONDS, addEmailErrorMessage, formatPhoneDisplay, isValidEmail } from "@/lib/phoneAuth";

const AUTH_CALLBACK = "https://app.brxs.ca/auth/callback";

/**
 * Phone-OTP accounts must add and confirm an email before the workspace opens.
 * public.users.email is NOT NULL and billing, invites and vendor records are
 * keyed by email, so the profile row is only created once Supabase has a
 * confirmed email on the Auth user (updateUser → confirmation link).
 */
export default function AddEmailPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const p = palette(resolvedTheme === "dark");
  const [email, setEmail] = useState(user?.pending_email || "");
  const [sentTo, setSentTo] = useState(user?.pending_email || "");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendLink = async (e) => {
    e?.preventDefault?.();
    if (busy || cooldown > 0) return;
    const clean = email.trim().toLowerCase();
    setError(""); setInfo("");
    if (!isValidEmail(clean)) { setError("Enter a valid email address."); return; }
    setBusy(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ email: clean }, { emailRedirectTo: AUTH_CALLBACK });
      if (err) { setError(addEmailErrorMessage(err)); return; }
      setSentTo(clean);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setInfo(`We sent a confirmation link to ${clean}. Open it, then come back here.`);
    } finally {
      setBusy(false);
    }
  };

  const checkConfirmed = async () => {
    setError(""); setBusy(true);
    try {
      const { data, error: err } = await supabase.auth.refreshSession();
      if (err || !data?.session) { setError("Your session ended. Sign in again."); return; }
      const profile = await loadUserProfile(data.session);
      if (profile?.needs_email) {
        setError("That email isn't confirmed yet. Open the link in the email we sent.");
        return;
      }
      setUser(profile);
      navigate(profile?.needs_onboarding ? "/onboarding" : "/", { replace: true });
    } catch {
      setError("Could not check your email yet. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const input = {
    marginTop: 8, width: "100%", padding: "14px 16px", border: `1px solid ${p.hairS}`, borderRadius: 2,
    fontSize: 15, fontFamily: F.display, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box",
  };
  const primary = {
    padding: "13px 20px", background: T.brick, color: "#fff", border: "none", borderRadius: 2, fontSize: 14,
    fontWeight: 500, fontFamily: F.display, cursor: "pointer", width: "100%", opacity: busy ? 0.6 : 1,
  };
  const secondary = { ...primary, background: "transparent", color: p.ink, border: `1px solid ${p.hair}` };
  const link = { background: "none", border: "none", cursor: "pointer", color: p.ink, textDecoration: "underline", textUnderlineOffset: 3, fontSize: 12, padding: 0, fontFamily: F.display };

  return (
    <div data-testid="add-email-page" style={{ minHeight: "100vh", background: p.bg, color: p.ink, fontFamily: F.display, padding: "48px 56px", display: "flex", flexDirection: "column" }}>
      <Wordmark size={19} color={p.ink} />
      <form onSubmit={sendLink} style={{ marginTop: "auto", marginBottom: "auto", maxWidth: 440, display: "flex", flexDirection: "column", gap: 14 }}>
        <Over color={p.ink4}>One more step</Over>
        <div style={{ fontSize: 40, fontWeight: 500, letterSpacing: -1.2, lineHeight: 1.05 }}>Add your email.</div>
        <p style={{ fontSize: 14, color: p.ink3, margin: 0, lineHeight: 1.5 }}>
          {user?.phone ? `You're signed in with ${formatPhoneDisplay(user.phone)}. ` : ""}
          Receipts, invites and media delivery go to email, so confirm one to finish setting up.
        </p>
        <div>
          <Over color={p.ink3}>Email</Over>
          <input data-testid="add-email-input" type="email" autoComplete="email" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required style={input} />
        </div>
        {error && <p role="alert" data-testid="add-email-error" style={{ fontSize: 13, color: T.bad, margin: 0 }}>{error}</p>}
        {info && !error && <p style={{ fontSize: 13, color: T.ok, margin: 0 }}>{info}</p>}
        <button type="submit" data-testid="add-email-send" disabled={busy || cooldown > 0} style={primary}>
          {busy ? "…" : sentTo ? (cooldown > 0 ? `Resend link in ${cooldown}s` : "Resend confirmation link") : "Send confirmation link"}
        </button>
        {sentTo && (
          <button type="button" data-testid="add-email-confirmed" onClick={checkConfirmed} disabled={busy} style={secondary}>
            I've confirmed my email
          </button>
        )}
        <div style={{ fontSize: 12 }}>
          <button type="button" onClick={async () => { await logout(); navigate("/login", { replace: true }); }} style={link}>
            Sign out
          </button>
        </div>
      </form>
    </div>
  );
}
